import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileText, CheckCircle, AlertTriangle, 
  AlertCircle, Info, Download, Loader2, X, BookOpen, 
  ExternalLink, ChevronDown, ChevronUp, Scale, Lightbulb,
  Trophy, TrendingUp, Repeat, Clock, BarChart3, Sparkles, History
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import SpEdKitLogo from '../components/SpEdKitLogo';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Severity color mapping
const severityColors = {
  Critical: 'bg-red-100 text-red-800 border-red-200',
  High: 'bg-orange-100 text-orange-800 border-orange-200',
  Medium: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  Low: 'bg-blue-100 text-blue-800 border-blue-200',
};

const severityIcons = {
  Critical: <AlertCircle className="w-4 h-4 text-red-600" />,
  High: <AlertTriangle className="w-4 h-4 text-orange-600" />,
  Medium: <Info className="w-4 h-4 text-yellow-600" />,
  Low: <Info className="w-4 h-4 text-blue-600" />,
};

// Component to render text with glossary term tooltips
const GlossaryText = ({ text, glossary }) => {
  if (!text || !glossary) return <span>{text}</span>;
  
  // Create a regex pattern from glossary terms
  const terms = Object.keys(glossary).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`\\b(${terms.join('|')})\\b`, 'gi');
  
  const parts = text.split(pattern);
  
  return (
    <span>
      {parts.map((part, index) => {
        const upperPart = part.toUpperCase();
        const glossaryEntry = glossary[upperPart] || glossary[part];
        
        if (glossaryEntry) {
          return (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="underline decoration-dotted decoration-primary/50 cursor-help font-medium text-primary">
                    {part}
                  </span>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm p-4 bg-white border shadow-lg">
                  <div className="space-y-2">
                    <p className="font-bold text-primary">{glossaryEntry.term}</p>
                    <p className="text-sm text-text-secondary">{glossaryEntry.definition}</p>
                    {glossaryEntry.example && (
                      <p className="text-xs text-text-muted italic">
                        Example: {glossaryEntry.example}
                      </p>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </span>
  );
};

const IEPReview = ({ user }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisProgress, setAnalysisProgress] = useState({ processed: 0, total: 0 });
  const [extractedText, setExtractedText] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [review, setReview] = useState(null);
  const [glossary, setGlossary] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [expandedConcerns, setExpandedConcerns] = useState({});
  const [activeTab, setActiveTab] = useState('review');
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showFullReview, setShowFullReview] = useState(false);

  useEffect(() => {
    // Fetch glossary on mount
    fetchGlossary();
    // Fetch insights on mount
    fetchInsights();
  }, []);

  const fetchGlossary = async () => {
    try {
      const response = await axios.get(`${API}/iep-review/glossary`);
      setGlossary(response.data.glossary || {});
    } catch (error) {
      console.error('Error fetching glossary:', error);
    }
  };

  const fetchInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.get(`${API}/iep-review-insights`, { withCredentials: true });
      setInsights(response.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoadingInsights(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (selectedFile) => {
    if (!selectedFile.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    
    setFile(selectedFile);
    setUploading(true);
    setReview(null);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      const response = await axios.post(`${API}/iep-review/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setExtractedText(response.data.extracted_text);
      setDocumentName(response.data.document_name);
      toast.success(`Document uploaded: ${response.data.page_count} pages extracted`);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleAnalyze = async () => {
    if (!extractedText) {
      toast.error('Please upload a document first');
      return;
    }
    
    setAnalyzing(true);
    setAnalysisProgress({ processed: 0, total: 0 });
    const docLength = extractedText.length;
    const estimatedChunks = Math.ceil(docLength / 10000);
    
    try {
      // Start the analysis job
      const startResponse = await axios.post(`${API}/iep-review/analyze`, {
        document_text: extractedText,
        document_name: documentName
      }, { withCredentials: true });
      
      const jobId = startResponse.data.job_id;
      const totalChunks = startResponse.data.document_stats?.sections_to_analyze || 1;
      setAnalysisProgress({ processed: 0, total: totalChunks });
      
      if (totalChunks > 1) {
        toast.info(`Analyzing full document (${totalChunks} sections)...`);
      }
      
      // Poll for job completion
      let completed = false;
      let pollCount = 0;
      const maxPolls = 120; // 10 minutes max (5s intervals)
      
      while (!completed && pollCount < maxPolls) {
        await new Promise(resolve => setTimeout(resolve, 5000)); // Wait 5 seconds
        pollCount++;
        
        try {
          const statusResponse = await axios.get(`${API}/iep-review/job/${jobId}`, { withCredentials: true });
          const jobStatus = statusResponse.data;
          
          // Update progress
          if (jobStatus.progress) {
            setAnalysisProgress({
              processed: jobStatus.progress.processed_chunks || 0,
              total: jobStatus.progress.total_chunks || totalChunks
            });
          }
          
          if (jobStatus.status === 'completed') {
            completed = true;
            setReview(jobStatus.review);
            if (jobStatus.glossary) {
              setGlossary(jobStatus.glossary);
            }
            toast.success(`Analysis complete! Reviewed ${jobStatus.progress?.total_chunks || 1} sections of your IEP.`);
          } else if (jobStatus.status === 'failed') {
            throw new Error(jobStatus.error || 'Analysis failed');
          }
          // If still processing, continue polling
        } catch (pollError) {
          if (pollError.response?.status === 404) {
            throw new Error('Analysis job not found');
          }
          // For other errors, continue polling
          console.warn('Poll error, retrying...', pollError);
        }
      }
      
      if (!completed) {
        throw new Error('Analysis timed out after 10 minutes');
      }
      
    } catch (error) {
      console.error('Error analyzing document:', error);
      toast.error(error.message || error.response?.data?.detail || 'Failed to analyze document');
    } finally {
      setAnalyzing(false);
      setAnalysisProgress({ processed: 0, total: 0 });
    }
  };

  const handleDownloadPDF = () => {
    if (!review) return;
    
    // Create printable HTML content
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>IEP Compliance Review - ${documentName}</title>
        <style>
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            line-height: 1.6;
            color: #1a1a1a;
            max-width: 800px;
            margin: 0 auto;
            padding: 40px 20px;
          }
          h1 {
            color: #4A7C59;
            border-bottom: 3px solid #4A7C59;
            padding-bottom: 10px;
          }
          h2 {
            color: #2d5a3d;
            margin-top: 30px;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
          }
          h3 {
            color: #444;
            margin-top: 20px;
          }
          .summary-box {
            background: #f5f9f6;
            border: 1px solid #4A7C59;
            border-radius: 8px;
            padding: 20px;
            margin: 20px 0;
          }
          .score {
            font-size: 1.2em;
            font-weight: bold;
            color: #4A7C59;
          }
          .strength {
            background: #ecfdf5;
            border-left: 4px solid #10b981;
            padding: 15px;
            margin: 15px 0;
            border-radius: 0 8px 8px 0;
          }
          .concern {
            border-left: 4px solid;
            padding: 15px;
            margin: 15px 0;
            border-radius: 0 8px 8px 0;
          }
          .concern.critical { background: #fef2f2; border-color: #ef4444; }
          .concern.high { background: #fff7ed; border-color: #f97316; }
          .concern.medium { background: #fefce8; border-color: #eab308; }
          .concern.low { background: #eff6ff; border-color: #3b82f6; }
          .severity {
            display: inline-block;
            padding: 2px 8px;
            border-radius: 4px;
            font-size: 0.8em;
            font-weight: bold;
            margin-bottom: 10px;
          }
          .severity.critical { background: #fee2e2; color: #991b1b; }
          .severity.high { background: #ffedd5; color: #9a3412; }
          .severity.medium { background: #fef9c3; color: #854d0e; }
          .severity.low { background: #dbeafe; color: #1e40af; }
          .recommendation {
            background: #f0fdf4;
            border: 1px solid #86efac;
            padding: 10px;
            margin-top: 10px;
            border-radius: 4px;
          }
          .legal-ref {
            font-style: italic;
            color: #666;
            font-size: 0.9em;
          }
          .resource-link {
            color: #4A7C59;
          }
          .next-steps {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 20px;
            margin-top: 30px;
          }
          .next-steps ol {
            margin: 0;
            padding-left: 20px;
          }
          .next-steps li {
            margin: 8px 0;
          }
          .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            font-size: 0.85em;
            color: #666;
            text-align: center;
          }
          @media print {
            body { padding: 20px; }
            .concern, .strength { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>🌱 IEP Compliance Review</h1>
        <p><strong>Document:</strong> ${documentName}</p>
        <p><strong>Review Date:</strong> ${new Date().toLocaleDateString()}</p>
        
        <div class="summary-box">
          <h2 style="margin-top: 0; border: none;">Summary</h2>
          <p>${review.summary?.overall_assessment || 'No summary available'}</p>
          <p class="score">Compliance Assessment: ${review.summary?.compliance_score || 'N/A'}</p>
          <p>
            <strong>Findings:</strong> 
            ${review.summary?.key_findings_count?.strengths || 0} Strengths, 
            ${review.summary?.key_findings_count?.concerns || 0} Areas of Concern,
            ${review.summary?.key_findings_count?.critical_issues || 0} Critical Issues
          </p>
        </div>
        
        <h2>✓ Strengths</h2>
        ${(review.strengths || []).map(s => `
          <div class="strength">
            <h3>${s.area}</h3>
            <p>${s.finding}</p>
            <p><strong>Why it matters:</strong> ${s.why_it_matters}</p>
          </div>
        `).join('')}
        
        <h2>⚠ Areas of Concern</h2>
        ${(review.concerns || []).map(c => `
          <div class="concern ${c.severity?.toLowerCase()}">
            <span class="severity ${c.severity?.toLowerCase()}">${c.severity}</span>
            <h3>${c.area}</h3>
            <p><strong>Issue:</strong> ${c.issue}</p>
            <p class="legal-ref"><strong>Legal Basis:</strong> ${c.legal_basis}</p>
            <p>${c.plain_language_explanation}</p>
            <div class="recommendation">
              <strong>Recommendation:</strong> ${c.recommendation}
            </div>
            ${c.resource_url ? `<p><strong>Learn More:</strong> <a class="resource-link" href="${c.resource_url}">${c.resource_title || c.resource_url}</a></p>` : ''}
          </div>
        `).join('')}
        
        <div class="next-steps">
          <h2 style="margin-top: 0;">📋 Recommended Next Steps</h2>
          <ol>
            ${(review.recommended_next_steps || []).map(step => `<li>${step}</li>`).join('')}
          </ol>
        </div>
        
        <div class="footer">
          <p>Generated by SpEdKit IEP Compliance Review Tool</p>
          <p>This tool is designed for educational self-evaluation only and does not constitute legal advice. Please consult with your Program Specialist if you have specific questions.</p>
        </div>
      </body>
      </html>
    `;
    
    // Open in new window for printing/saving as PDF
    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const toggleConcern = (index) => {
    setExpandedConcerns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const clearReview = () => {
    setFile(null);
    setExtractedText('');
    setDocumentName('');
    setReview(null);
    setShowFullReview(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-secondary/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="text-text-muted hover:text-primary">
                <ArrowLeft className="w-5 h-5" />
              </button>
              <SpEdKitLogo size="sm" />
            </div>
            {review && (
              <Button
                onClick={handleDownloadPDF}
                className="bg-primary text-white hover:bg-primary/90 rounded-full"
                data-testid="download-pdf-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-6 animate-fade-in-up">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] rounded-xl flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-text-primary">
                IEP Compliance Review
              </h1>
              <p className="text-text-secondary text-sm">
                Upload your drafted IEP for expert feedback on compliance and best practices
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-8">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-6">
            <TabsTrigger value="review" className="flex items-center gap-2" data-testid="review-tab">
              <Scale className="w-4 h-4" />
              New Review
            </TabsTrigger>
            <TabsTrigger value="insights" className="flex items-center gap-2" data-testid="insights-tab">
              <Sparkles className="w-4 h-4" />
              My Progress
              {insights?.celebrations?.length > 0 && (
                <Badge className="bg-green-100 text-green-700 text-xs ml-1">
                  {insights.celebrations.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Review Tab Content */}
          <TabsContent value="review" className="animate-fade-in-up">
            {/* Upload Section */}
            {!review && (
              <div className="mb-8">
                <div
                  className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all ${
                    dragActive 
                      ? 'border-primary bg-primary/5' 
                      : file 
                        ? 'border-green-300 bg-green-50' 
                        : 'border-gray-200 hover:border-primary/50 hover:bg-gray-50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                  data-testid="upload-dropzone"
                >
              {uploading ? (
                <div className="flex flex-col items-center gap-4">
                  <Loader2 className="w-12 h-12 text-primary animate-spin" />
                  <p className="text-text-secondary">Extracting document text...</p>
                </div>
              ) : file ? (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                    <FileText className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-text-primary">{file.name}</p>
                    <p className="text-sm text-text-muted">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={clearReview}
                      variant="outline"
                      size="sm"
                      className="rounded-full"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                    <Button
                      onClick={handleAnalyze}
                      disabled={analyzing || !extractedText}
                      className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-full"
                      data-testid="analyze-btn"
                    >
                      {analyzing ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Analyzing...
                        </>
                      ) : (
                        <>
                          <Scale className="w-4 h-4 mr-2" />
                          Analyze IEP
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center">
                    <Upload className="w-8 h-8 text-gray-400" />
                  </div>
                  <div>
                    <p className="font-semibold text-text-primary">
                      Drop your IEP document here
                    </p>
                    <p className="text-sm text-text-muted">
                      or click to browse (PDF only)
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handleFileInput}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    variant="outline"
                    className="rounded-full"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Select PDF File
                  </Button>
                </div>
              )}
            </div>
            
            {/* Info Box */}
            <div className="mt-6 bg-[#6366F1]/5 border border-[#6366F1]/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-[#6366F1] flex-shrink-0 mt-0.5" />
                <div className="text-sm text-text-secondary">
                  <p className="font-medium text-text-primary mb-1">What this review covers:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Compliance with <GlossaryText text="IDEA" glossary={glossary} /> federal requirements</li>
                    <li>California Education Code alignment</li>
                    <li>Ninth Circuit case law standards</li>
                    <li>Best practices for <GlossaryText text="FAPE" glossary={glossary} /> and <GlossaryText text="LRE" glossary={glossary} /></li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Loading */}
        {analyzing && (
          <div className="flex flex-col items-center justify-center py-16 animate-fade-in-up">
            <Loader2 className="w-16 h-16 text-[#6366F1] animate-spin mb-6" />
            <h2 className="font-heading font-bold text-xl text-text-primary mb-2">
              Analyzing Your Complete IEP Document
            </h2>
            <p className="text-text-secondary text-center max-w-md mb-4">
              Reviewing every section for compliance with IDEA, California Education Code, and Ninth Circuit case law standards...
            </p>
            {analysisProgress.total > 0 && (
              <div className="w-64 mb-4">
                <div className="flex justify-between text-sm text-text-muted mb-1">
                  <span>Progress</span>
                  <span>{analysisProgress.processed} / {analysisProgress.total} sections</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-[#6366F1] h-2 rounded-full transition-all duration-500"
                    style={{ width: `${(analysisProgress.processed / analysisProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
            <p className="text-sm text-text-muted text-center">
              {analysisProgress.total > 1 
                ? `Analyzing section ${analysisProgress.processed + 1} of ${analysisProgress.total}...`
                : 'This thorough review may take 1-2 minutes for longer documents.'}
            </p>
          </div>
        )}

        {/* Review Results */}
        {review && !analyzing && (
          <div className="space-y-8 animate-fade-in-up">
            {/* Document Info & Actions */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#6366F1]" />
                <div>
                  <p className="font-semibold text-text-primary">{documentName}</p>
                  <p className="text-sm text-text-muted">Review completed</p>
                </div>
              </div>
              <Button
                onClick={clearReview}
                variant="outline"
                className="rounded-full"
              >
                <Upload className="w-4 h-4 mr-2" />
                Review Another IEP
              </Button>
            </div>

            {/* Summary Card */}
            <div className="bg-gradient-to-br from-[#6366F1]/10 to-[#8B5CF6]/10 rounded-2xl p-6 border border-[#6366F1]/20">
              <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#6366F1]" />
                Summary
              </h2>
              <p className="text-text-secondary mb-4">
                <GlossaryText text={review.summary?.overall_assessment} glossary={glossary} />
              </p>
              <div className="flex flex-wrap gap-4">
                <div className="bg-white rounded-lg px-4 py-2 border">
                  <p className="text-xs text-text-muted">Compliance Score</p>
                  <p className={`font-bold ${
                    review.summary?.compliance_score === 'Strong' ? 'text-green-600' :
                    review.summary?.compliance_score === 'Adequate' ? 'text-blue-600' :
                    review.summary?.compliance_score === 'Needs Improvement' ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {review.summary?.compliance_score || 'N/A'}
                  </p>
                </div>
                <div className="bg-white rounded-lg px-4 py-2 border">
                  <p className="text-xs text-text-muted">Strengths</p>
                  <p className="font-bold text-green-600">
                    {review.summary?.key_findings_count?.strengths || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg px-4 py-2 border">
                  <p className="text-xs text-text-muted">Concerns</p>
                  <p className="font-bold text-yellow-600">
                    {review.summary?.key_findings_count?.concerns || 0}
                  </p>
                </div>
                <div className="bg-white rounded-lg px-4 py-2 border">
                  <p className="text-xs text-text-muted">Critical Issues</p>
                  <p className="font-bold text-red-600">
                    {review.summary?.key_findings_count?.critical_issues || 0}
                  </p>
                </div>
              </div>
            </div>

            {/* Strengths Section */}
            {review.strengths?.length > 0 && (
              <div>
                <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Strengths
                </h2>
                <div className="space-y-4">
                  {review.strengths.map((strength, index) => (
                    <div 
                      key={index}
                      className="bg-green-50 border border-green-200 rounded-xl p-5"
                    >
                      <div className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div>
                          <Badge className="bg-green-100 text-green-800 border-green-200 mb-2">
                            {strength.area}
                          </Badge>
                          <p className="text-text-primary font-medium mb-2">
                            <GlossaryText text={strength.finding} glossary={glossary} />
                          </p>
                          <p className="text-sm text-text-secondary">
                            <span className="font-medium">Why it matters: </span>
                            <GlossaryText text={strength.why_it_matters} glossary={glossary} />
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Concerns Section - Show top 5 critical ones initially */}
            {review.concerns?.length > 0 && (
              <div>
                <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  {showFullReview ? 'All Areas of Concern' : 'Top Growth Areas'}
                  {!showFullReview && review.concerns.length > 5 && (
                    <Badge variant="outline" className="ml-2 text-xs">
                      Showing 5 of {review.concerns.length}
                    </Badge>
                  )}
                </h2>
                <div className="space-y-4">
                  {(() => {
                    // Sort concerns by severity (Critical > High > Medium > Low) to ensure top 5 are most critical
                    const severityOrder = { 'Critical': 0, 'High': 1, 'Medium': 2, 'Low': 3 };
                    const sortedConcerns = [...(review.concerns || [])].sort(
                      (a, b) => (severityOrder[a.severity] || 4) - (severityOrder[b.severity] || 4)
                    );
                    const displayConcerns = showFullReview ? sortedConcerns : sortedConcerns.slice(0, 5);
                    return displayConcerns.map((concern, index) => (
                    <Collapsible 
                      key={index}
                      open={expandedConcerns[index] !== false}
                      onOpenChange={() => toggleConcern(index)}
                    >
                      <div className={`rounded-xl border-2 overflow-hidden ${
                        concern.severity === 'Critical' ? 'border-red-200 bg-red-50' :
                        concern.severity === 'High' ? 'border-orange-200 bg-orange-50' :
                        concern.severity === 'Medium' ? 'border-yellow-200 bg-yellow-50' :
                        'border-blue-200 bg-blue-50'
                      }`}>
                        <CollapsibleTrigger className="w-full p-5 text-left">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              {severityIcons[concern.severity]}
                              <div>
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge className={severityColors[concern.severity]}>
                                    {concern.severity}
                                  </Badge>
                                  <Badge variant="outline">{concern.area}</Badge>
                                </div>
                                <p className="font-medium text-text-primary">
                                  <GlossaryText text={concern.issue} glossary={glossary} />
                                </p>
                              </div>
                            </div>
                            {expandedConcerns[index] === false ? (
                              <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0" />
                            ) : (
                              <ChevronUp className="w-5 h-5 text-text-muted flex-shrink-0" />
                            )}
                          </div>
                        </CollapsibleTrigger>
                        
                        <CollapsibleContent>
                          <div className="px-5 pb-5 pt-0 space-y-4 border-t border-current/10">
                            {/* Legal Basis */}
                            <div className="pt-4">
                              <p className="text-sm font-medium text-text-muted mb-1">Legal Basis</p>
                              <p className="text-sm text-text-secondary italic">
                                <GlossaryText text={concern.legal_basis} glossary={glossary} />
                              </p>
                            </div>
                            
                            {/* Plain Language Explanation */}
                            <div>
                              <p className="text-sm font-medium text-text-muted mb-1">What This Means</p>
                              <p className="text-text-secondary">
                                <GlossaryText text={concern.plain_language_explanation} glossary={glossary} />
                              </p>
                            </div>
                            
                            {/* Recommendation */}
                            <div className="bg-white/50 rounded-lg p-4 border border-current/10">
                              <div className="flex items-start gap-2">
                                <Lightbulb className="w-5 h-5 text-amber-600 flex-shrink-0" />
                                <div>
                                  <p className="text-sm font-medium text-text-primary mb-1">How to Fix This</p>
                                  <p className="text-text-secondary">
                                    <GlossaryText text={concern.recommendation} glossary={glossary} />
                                  </p>
                                </div>
                              </div>
                            </div>
                            
                            {/* Resource Link */}
                            {concern.resource_url && (
                              <a
                                href={concern.resource_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-[#6366F1] hover:underline"
                              >
                                <ExternalLink className="w-4 h-4" />
                                {concern.resource_title || 'Learn More About This Topic'}
                              </a>
                            )}
                          </div>
                        </CollapsibleContent>
                      </div>
                    </Collapsible>
                  ));
                  })()}
                </div>
              </div>
            )}

            {/* Next Steps - Only show in full review mode */}
            {showFullReview && review.recommended_next_steps?.length > 0 && (
              <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200">
                <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Recommended Next Steps
                </h2>
                <ol className="list-decimal list-inside space-y-3">
                  {review.recommended_next_steps.map((step, index) => (
                    <li key={index} className="text-text-secondary">
                      <GlossaryText text={step} glossary={glossary} />
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Procedural Checklist */}
            {review.procedural_checklist?.checklist && (
              <div className="bg-white rounded-2xl p-6 border border-gray-200">
                <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-primary" />
                  Procedural Error Checklist
                </h2>
                <p className="text-sm text-text-muted mb-4">
                  Key procedural items required for IEP compliance in California
                </p>
                
                {/* Summary */}
                {review.procedural_checklist.summary && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                    <div className="bg-green-50 rounded-lg p-3 text-center border border-green-200">
                      <p className="text-xl font-bold text-green-600">{review.procedural_checklist.summary.passed || 0}</p>
                      <p className="text-xs text-green-700">Passed</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-200">
                      <p className="text-xl font-bold text-red-600">{review.procedural_checklist.summary.failed || 0}</p>
                      <p className="text-xs text-red-700">Failed</p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-200">
                      <p className="text-xl font-bold text-gray-600">{review.procedural_checklist.summary.not_applicable || 0}</p>
                      <p className="text-xs text-gray-600">N/A</p>
                    </div>
                    <div className="bg-amber-50 rounded-lg p-3 text-center border border-amber-200">
                      <p className="text-xl font-bold text-amber-600">{review.procedural_checklist.summary.unable_to_determine || 0}</p>
                      <p className="text-xs text-amber-700">Needs Review</p>
                    </div>
                  </div>
                )}
                
                {/* Checklist Items */}
                <div className="space-y-3">
                  {review.procedural_checklist.checklist.map((item, index) => (
                    <div 
                      key={index} 
                      className={`p-4 rounded-lg border ${
                        item.status === 'pass' ? 'bg-green-50 border-green-200' :
                        item.status === 'fail' ? 'bg-red-50 border-red-200' :
                        item.status === 'not_applicable' ? 'bg-gray-50 border-gray-200' :
                        'bg-amber-50 border-amber-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                          item.status === 'pass' ? 'bg-green-500 text-white' :
                          item.status === 'fail' ? 'bg-red-500 text-white' :
                          item.status === 'not_applicable' ? 'bg-gray-400 text-white' :
                          'bg-amber-500 text-white'
                        }`}>
                          {item.status === 'pass' ? '✓' : 
                           item.status === 'fail' ? '✗' : 
                           item.status === 'not_applicable' ? '−' : '?'}
                        </div>
                        <div className="flex-1">
                          <p className={`font-medium ${
                            item.status === 'pass' ? 'text-green-800' :
                            item.status === 'fail' ? 'text-red-800' :
                            item.status === 'not_applicable' ? 'text-gray-600' :
                            'text-amber-800'
                          }`}>
                            {item.item}
                          </p>
                          <p className={`text-sm mt-1 ${
                            item.status === 'pass' ? 'text-green-700' :
                            item.status === 'fail' ? 'text-red-700' :
                            item.status === 'not_applicable' ? 'text-gray-500' :
                            'text-amber-700'
                          }`}>
                            {item.details}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Show More Feedback Button - Appears after procedural checklist */}
            {!showFullReview && (review.concerns?.length > 5 || review.recommended_next_steps?.length > 0) && (
              <div className="text-center">
                <Button
                  onClick={() => setShowFullReview(true)}
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base border-2 border-[#6366F1] text-[#6366F1] hover:bg-[#6366F1]/10"
                  data-testid="show-more-feedback-btn"
                >
                  <ChevronDown className="w-5 h-5 mr-2" />
                  Show Me More Feedback
                  {review.concerns?.length > 5 && (
                    <Badge className="ml-2 bg-[#6366F1]/20 text-[#6366F1]">
                      +{review.concerns.length - 5} more
                    </Badge>
                  )}
                </Button>
                <p className="text-xs text-text-muted mt-2">
                  View all {review.concerns?.length || 0} concerns and detailed next steps
                </p>
              </div>
            )}

            {/* Collapse Button - When full review is showing */}
            {showFullReview && (
              <div className="text-center">
                <Button
                  onClick={() => setShowFullReview(false)}
                  variant="ghost"
                  className="rounded-full text-text-muted hover:text-text-primary"
                  data-testid="show-less-feedback-btn"
                >
                  <ChevronUp className="w-4 h-4 mr-2" />
                  Show Less
                </Button>
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <Info className="w-5 h-5 flex-shrink-0" />
                <p>
                  <strong>Disclaimer:</strong> This tool is designed for educational self-evaluation only and does not 
                  constitute legal advice. Please consult with your Program Specialist if you have specific questions 
                  about the information provided by this tool.
                </p>
              </div>
            </div>
          </div>
        )}
          </TabsContent>

          {/* Insights Tab Content */}
          <TabsContent value="insights" className="animate-fade-in-up">
            {loadingInsights ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="w-12 h-12 text-[#6366F1] animate-spin mb-4" />
                <p className="text-text-secondary">Loading your progress insights...</p>
              </div>
            ) : insights ? (
              <div className="space-y-8">
                {/* Summary Stats Header */}
                <div className="bg-gradient-to-br from-[#6366F1]/10 to-[#8B5CF6]/10 rounded-2xl p-6 border border-[#6366F1]/20">
                  <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-[#6366F1]" />
                    Your Review Journey
                  </h2>
                  <p className="text-text-secondary mb-4">{insights.message}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-xl p-4 border">
                      <p className="text-2xl font-bold text-[#6366F1]">{insights.total_reviews}</p>
                      <p className="text-xs text-text-muted">Total Reviews</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border">
                      <p className="text-2xl font-bold text-green-600">{insights.summary_stats?.total_strengths_all_time || 0}</p>
                      <p className="text-xs text-text-muted">Strengths Found</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border">
                      <p className="text-2xl font-bold text-amber-600">{insights.summary_stats?.total_concerns_all_time || 0}</p>
                      <p className="text-xs text-text-muted">Concerns Identified</p>
                    </div>
                    <div className="bg-white rounded-xl p-4 border">
                      <p className="text-2xl font-bold text-purple-600">{insights.celebrations?.length || 0}</p>
                      <p className="text-xs text-text-muted">Improvements Made</p>
                    </div>
                  </div>
                </div>

                {/* Celebrations Section */}
                {insights.celebrations?.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                      <Trophy className="w-5 h-5 text-amber-500" />
                      Celebrations
                    </h2>
                    <div className="space-y-4">
                      {insights.celebrations.map((celebration, index) => (
                        <div 
                          key={index}
                          className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-5"
                        >
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                              {celebration.type === 'correction' ? (
                                <CheckCircle className="w-6 h-6 text-amber-600" />
                              ) : (
                                <TrendingUp className="w-6 h-6 text-amber-600" />
                              )}
                            </div>
                            <div className="flex-1">
                              <Badge className="bg-amber-100 text-amber-800 border-amber-200 mb-2">
                                {celebration.type === 'correction' ? 'Issue Corrected!' : 'Improvement!'}
                              </Badge>
                              <p className="font-medium text-text-primary mb-2">{celebration.message}</p>
                              
                              {celebration.type === 'correction' && (
                                <div className="bg-white/50 rounded-lg p-3 mt-3 space-y-2 text-sm">
                                  <div className="flex items-start gap-2">
                                    <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-text-muted text-xs">Previous concern ({new Date(celebration.original_date).toLocaleDateString()}):</p>
                                      <p className="text-text-secondary">{celebration.original_concern}</p>
                                    </div>
                                  </div>
                                  <div className="flex items-start gap-2">
                                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                                    <div>
                                      <p className="text-text-muted text-xs">Now a strength ({new Date(celebration.current_date).toLocaleDateString()}):</p>
                                      <p className="text-text-secondary">{celebration.current_strength}</p>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Improvement Trends */}
                {insights.improvement_trends?.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      Positive Trends
                    </h2>
                    <div className="space-y-3">
                      {insights.improvement_trends.map((trend, index) => (
                        <div 
                          key={index}
                          className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3"
                        >
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="text-text-primary font-medium">{trend.message}</p>
                            {trend.percentage_change && (
                              <p className="text-sm text-green-600">
                                {trend.percentage_change}% reduction in concerns
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recurring Patterns (Areas to Focus On) */}
                {insights.recurring_patterns?.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                      <Repeat className="w-5 h-5 text-orange-500" />
                      Areas Needing Attention
                    </h2>
                    <p className="text-text-secondary text-sm mb-4">
                      These areas have appeared as concerns in multiple reviews. Focusing on them could significantly improve your IEPs.
                    </p>
                    <div className="space-y-4">
                      {insights.recurring_patterns.map((pattern, index) => (
                        <Collapsible key={index}>
                          <div className="bg-orange-50 border border-orange-200 rounded-xl overflow-hidden">
                            <CollapsibleTrigger className="w-full p-4 text-left">
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-orange-600" />
                                  </div>
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <p className="font-medium text-text-primary">{pattern.area}</p>
                                      <Badge className={severityColors[pattern.typical_severity]}>
                                        {pattern.typical_severity}
                                      </Badge>
                                    </div>
                                    <p className="text-sm text-text-secondary">{pattern.message}</p>
                                  </div>
                                </div>
                                <Badge variant="outline" className="flex-shrink-0">
                                  {pattern.occurrence_count}x
                                </Badge>
                              </div>
                            </CollapsibleTrigger>
                            <CollapsibleContent>
                              <div className="px-4 pb-4 pt-0 border-t border-orange-100">
                                <div className="mt-4 space-y-3">
                                  <div>
                                    <p className="text-xs text-text-muted mb-1">Example issues found:</p>
                                    <ul className="list-disc list-inside text-sm text-text-secondary space-y-1">
                                      {pattern.sample_issues?.map((issue, i) => (
                                        <li key={i}>{issue}</li>
                                      ))}
                                    </ul>
                                  </div>
                                  <div className="bg-white rounded-lg p-3 border border-orange-100">
                                    <div className="flex items-start gap-2">
                                      <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                      <div>
                                        <p className="text-xs text-text-muted">Recommendation:</p>
                                        <p className="text-sm text-text-primary">{pattern.recommendation}</p>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-text-muted">
                                    <span className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      First seen: {new Date(pattern.first_seen).toLocaleDateString()}
                                    </span>
                                    <span className="flex items-center gap-1">
                                      <History className="w-3 h-3" />
                                      Last seen: {new Date(pattern.last_seen).toLocaleDateString()}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </CollapsibleContent>
                          </div>
                        </Collapsible>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Concern Areas */}
                {insights.summary_stats?.top_concern_areas?.length > 0 && (
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl border p-5">
                      <h3 className="font-heading font-semibold text-text-primary mb-3 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" />
                        Most Common Concern Areas
                      </h3>
                      <div className="space-y-2">
                        {insights.summary_stats.top_concern_areas.map((item, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <span className="text-sm text-text-secondary">{item.area}</span>
                            <Badge variant="outline">{item.count}</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {insights.summary_stats?.top_strength_areas?.length > 0 && (
                      <div className="bg-white rounded-xl border p-5">
                        <h3 className="font-heading font-semibold text-text-primary mb-3 flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Most Common Strength Areas
                        </h3>
                        <div className="space-y-2">
                          {insights.summary_stats.top_strength_areas.map((item, index) => (
                            <div key={index} className="flex items-center justify-between">
                              <span className="text-sm text-text-secondary">{item.area}</span>
                              <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                {item.count}
                              </Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Review Timeline */}
                {insights.review_timeline?.length > 0 && (
                  <div>
                    <h2 className="font-heading font-bold text-xl text-text-primary mb-4 flex items-center gap-2">
                      <History className="w-5 h-5 text-[#6366F1]" />
                      Review History
                    </h2>
                    <div className="space-y-3">
                      {insights.review_timeline.map((review, index) => (
                        <div 
                          key={index}
                          className="bg-white border rounded-xl p-4 flex items-center justify-between gap-4 hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-[#6366F1]/10 rounded-full flex items-center justify-center">
                              <FileText className="w-5 h-5 text-[#6366F1]" />
                            </div>
                            <div>
                              <p className="font-medium text-text-primary text-sm">{review.document_name}</p>
                              <p className="text-xs text-text-muted">
                                {new Date(review.date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <Badge className={
                                review.compliance_score === 'Strong' ? 'bg-green-100 text-green-800' :
                                review.compliance_score === 'Adequate' ? 'bg-blue-100 text-blue-800' :
                                review.compliance_score === 'Needs Improvement' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }>
                                {review.compliance_score}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                              <span className="flex items-center gap-1 text-green-600">
                                <CheckCircle className="w-3 h-3" />
                                {review.strength_count}
                              </span>
                              <span className="flex items-center gap-1 text-amber-600">
                                <AlertTriangle className="w-3 h-3" />
                                {review.concern_count}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {insights.total_reviews === 0 && (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 bg-[#6366F1]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Scale className="w-10 h-10 text-[#6366F1]" />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-text-primary mb-2">
                      Start Your Review Journey
                    </h3>
                    <p className="text-text-secondary mb-6 max-w-md mx-auto">
                      Upload your first IEP document for review. As you review more IEPs, we&apos;ll track your progress and help you identify patterns.
                    </p>
                    <Button 
                      onClick={() => setActiveTab('review')}
                      className="bg-[#6366F1] text-white hover:bg-[#6366F1]/90 rounded-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      Review Your First IEP
                    </Button>
                  </div>
                )}

                {/* No Insights Yet State */}
                {insights.total_reviews > 0 && !insights.has_insights && (
                  <div className="bg-[#6366F1]/5 border border-[#6366F1]/20 rounded-xl p-6 text-center">
                    <Sparkles className="w-10 h-10 text-[#6366F1] mx-auto mb-3" />
                    <h3 className="font-heading font-semibold text-text-primary mb-2">
                      Keep Reviewing to Unlock Insights!
                    </h3>
                    <p className="text-text-secondary text-sm max-w-md mx-auto">
                      After a few more reviews, we&apos;ll be able to identify patterns, celebrate your improvements, and provide personalized recommendations.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-text-secondary">Unable to load insights. Please try again.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default IEPReview;
